/**
 * ServiceMenu — 기능 탐색용 카테고리 그리드 (1단계)
 *
 * "자주 찾는 질문"이 문제 해결 동선이라면, 서비스 메뉴는 기능 탐색 동선이다.
 * 7개 카테고리를 2열 그리드로 노출하고, 각 카드는 클릭 시 해당 카테고리의
 * 상세 항목 리스트를 보여준다.
 *
 * 데이터: frontend/src/data/service-menu.json
 */

import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import type { ServiceCategory } from '@/types/qa';

interface ServiceMenuProps {
  categories: ServiceCategory[];
  onSelectCategory: (categoryId: string) => void;
}

export function ServiceMenu({ categories, onSelectCategory }: ServiceMenuProps) {
  const authStatus = useAuthStore((s) => s.status);

  return (
    <div className="ml-grid ml-grid-cols-2 ml-gap-2 ml-mt-2">
      {categories.map((cat) => (
        <CategoryCard
          key={cat.id}
          category={cat}
          authStatus={authStatus}
          onSelect={() => onSelectCategory(cat.id)}
        />
      ))}
    </div>
  );
}

interface CategoryCardProps {
  category: ServiceCategory;
  authStatus: 'authenticated' | 'anonymous';
  onSelect: () => void;
}

function CategoryCard({ category, authStatus, onSelect }: CategoryCardProps) {
  const isLocked =
    category.auth_required === 'required' && authStatus !== 'authenticated';

  const visibleItemsCount = category.items.filter((item) => {
    if (authStatus === 'authenticated') return true;
    return item.auth_required !== 'required';
  }).length;

  // 아이콘 영역의 폭(아이콘+gap)만큼 description/항목 수에 좌측 패딩을 줘 정렬 맞춤.
  // 아이콘이 빈 문자열이면 패딩 없이 좌측 정렬.
  const hasIcon = Boolean(category.icon);
  const subPadding = hasIcon ? 'ml-pl-6' : 'ml-pl-0';

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isLocked}
      className={cn(
        'ml-flex ml-flex-col ml-gap-1 ml-p-3 ml-text-left',
        'ml-bg-white ml-border ml-border-line ml-rounded-cb-md',
        'ml-cursor-pointer ml-transition-all ml-duration-150',
        'hover:ml-border-brand-600 hover:ml-bg-brand-50',
        'hover:ml--translate-y-px',
        isLocked &&
          'ml-opacity-50 ml-cursor-not-allowed hover:ml-bg-white hover:ml-border-line hover:ml-translate-y-0'
      )}
    >
      <div className="ml-flex ml-items-center ml-gap-2">
        {hasIcon && (
          <span className="ml-text-base ml-leading-none">{category.icon}</span>
        )}
        <span className="ml-text-cb-base ml-font-semibold ml-text-ink ml-flex-1 ml-truncate">
          {category.label}
        </span>
        {isLocked && <span className="ml-text-cb-xs">🔒</span>}
      </div>
      <div className={cn('ml-text-cb-xs ml-text-mute ml-truncate', subPadding)}>
        {category.description}
      </div>
      <div className={cn('ml-text-cb-xs ml-text-brand-500 ml-mt-0.5', subPadding)}>
        {visibleItemsCount}개 항목
      </div>
    </button>
  );
}
